
import React from 'react';
import { SummaryMetrics, ProcessedPageTiming } from '../types';
import { formatBytes, formatMilliseconds } from '../utils/formatters';
import {
  ClockIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  ServerIcon,
  TagIcon,
  ContentTypeIcon
} from './Icons';


interface SummaryViewProps {
  metrics: SummaryMetrics;
  pageTimings: ProcessedPageTiming[] | null;
  onShowErrorRequests: () => void;
}

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  unit?: string;
  onClick?: () => void;
  isClickable?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, icon, unit, onClick, isClickable }) => (
  <div 
    className={`bg-white p-4 rounded-lg shadow-md flex items-center space-x-3 transition-all duration-300 hover:shadow-lg hover:scale-105 ${isClickable ? 'cursor-pointer' : ''}`}
    onClick={onClick}
    role={isClickable ? "button" : undefined}
    tabIndex={isClickable ? 0 : undefined}
    onKeyDown={isClickable ? (e) => (e.key === 'Enter' || e.key === ' ') && onClick && onClick() : undefined}
    aria-label={isClickable ? `Show ${title}` : undefined}
  >
    <div className="p-2 bg-gray-100 rounded-full">{icon}</div>
    <div>
      <p className="text-sm text-gray-500">{title}</p>
      <p className="text-xl font-semibold text-gray-800">
        {value}
        {unit && <span className="text-sm text-gray-600 ml-1">{unit}</span>}
      </p>
    </div>
  </div>
);


const SummaryView: React.FC<SummaryViewProps> = ({ metrics, pageTimings, onShowErrorRequests }) => {
  if (!metrics) return null;

  const topContentTypes = Object.entries(metrics.contentTypes)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  const topDomains = Object.entries(metrics.domains)
    .sort(([,a], [,b]) => b - a)
    .slice(0,5);

  return (
    <div className="space-y-6 text-gray-800">
      <h2 className="text-2xl font-geomentia mb-4 text-violet-600">File Summary</h2>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <MetricCard title="Total Requests" value={metrics.totalRequests} icon={<DocumentDuplicateIcon className="h-6 w-6 text-violet-500" />} />
        <MetricCard title="Total Data Transferred" value={formatBytes(metrics.totalDataTransferred)} icon={<ServerIcon className="h-6 w-6 text-violet-500" />} />
        <MetricCard title="Avg. Response Time" value={formatMilliseconds(metrics.averageResponseTime)} icon={<ClockIcon className="h-6 w-6 text-violet-500" />} />
        <MetricCard 
          title="HTTP Errors" 
          value={metrics.errorCount} 
          icon={<ExclamationTriangleIcon className="h-6 w-6 text-red-500" />} 
          onClick={onShowErrorRequests}
          isClickable={metrics.errorCount > 0}
        />
         {metrics.totalLoadTime && (
           <MetricCard title="Total Page Load Time" value={formatMilliseconds(metrics.totalLoadTime)} icon={<ClockIcon className="h-6 w-6 text-violet-500" />} />
         )}
      </div>

      {pageTimings && pageTimings.length > 0 && (
        <div className="bg-white p-4 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-2 text-gray-800">Page Load Timings</h3>
          {pageTimings.map(pt => (
            <div key={pt.id} className="mb-2 p-2 border-b border-gray-200 last:border-b-0">
              <p className="text-md font-medium text-violet-500">{pt.title}</p>
              {pt.onContentLoad !== undefined && <p className="text-sm text-gray-700">DOM Content Loaded: <span className="font-semibold">{formatMilliseconds(pt.onContentLoad)}</span></p>}
              {pt.onLoad !== undefined && <p className="text-sm text-gray-700">Page Load (onLoad): <span className="font-semibold">{formatMilliseconds(pt.onLoad)}</span></p>}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-3 text-gray-800 flex items-center">
            <TagIcon className="h-5 w-5 mr-2 text-violet-500" />
            Requests by Method
          </h3>
          <ul className="space-y-1 text-sm">
            {Object.entries(metrics.requestsByMethod).map(([method, count]) => (
              <li key={method} className="flex justify-between">
                <span className="text-gray-700">{method}:</span>
                <span className="font-semibold text-violet-500">{count}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-3 text-gray-800 flex items-center">
            <ContentTypeIcon className="h-5 w-5 min-w-5 mr-2 text-violet-500" />
            Top Content Types
          </h3>
          <ul className="space-y-1 text-sm">
            {topContentTypes.map(([type, count]) => (
              <li key={type} className="flex justify-between">
                <span className="text-gray-700 truncate" title={type}>{type.length > 25 ? `${type.substring(0,25)}...` : type}:</span>
                <span className="font-semibold text-violet-500">{count}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
       <div className="bg-white p-4 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-3 text-gray-800 flex items-center">
            <ServerIcon className="h-5 w-5 mr-2 text-violet-500" />
            Top Domains
          </h3>
          <ul className="space-y-1 text-sm">
            {topDomains.map(([domain, count]) => (
              <li key={domain} className="flex justify-between">
                <span className="text-gray-700 truncate" title={domain}>{domain.length > 30 ? `${domain.substring(0,30)}...` : domain}:</span>
                <span className="font-semibold text-violet-500">{count}</span>
              </li>
            ))}
          </ul>
        </div>
    </div>
  );
};

export default SummaryView;
